import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, PencilSimple, Trash, X, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

const fmtEUR = (n) => (Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

export default function TiposSocio() {
  const [types, setTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: "", price: 0 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/member-types");
      setTypes(data || []);
    } catch (e) {
      logger.warn("Load member-types failed:", e?.message);
      toast.error("No se pudieron cargar los tipos");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ label: "", price: 0 });
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ label: t.label, price: t.price });
    setShowForm(true);
  };

  const close = () => {
    setShowForm(false);
    setEditing(null);
    setForm({ label: "", price: 0 });
  };

  const submit = async (e) => {
    e.preventDefault();
    const label = (form.label || "").trim();
    if (!label) { toast.error("La etiqueta es obligatoria"); return; }
    const price = Number(form.price);
    if (isNaN(price) || price < 0) { toast.error("Precio inválido"); return; }

    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/member-types/${editing.id}`, { label, price });
        toast.success("Tipo actualizado");
      } else {
        await api.post("/member-types", { label, price });
        toast.success("Tipo creado");
      }
      close();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (t.is_builtin) {
      toast.error("Los tipos predeterminados no se pueden eliminar");
      return;
    }
    if (t.usage_count > 0) {
      toast.error(`Hay ${t.usage_count} socio(s) usando este tipo. Reasígnalos primero.`);
      return;
    }
    if (!window.confirm(`¿Eliminar el tipo "${t.label}"?\n\nEsta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/member-types/${t.id}`);
      toast.success("Tipo eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#007AFF] font-bold">Configuración</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tighter">Tipos de Socio</h1>
          <p className="text-sm text-slate-500 mt-1">Gestión de categorías y precios · Solo administradores</p>
        </div>
        <button
          data-testid="add-tipo-btn"
          onClick={openCreate}
          className="bg-[#007AFF] hover:bg-[#005bb5] px-5 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm active:scale-[0.98] transition text-white"
          style={{ borderRadius: 6 }}
        >
          <Plus size={18} weight="bold" /> Nuevo Tipo
        </button>
      </div>

      <div className="bg-white border border-slate-200 overflow-hidden" style={{ borderRadius: 8 }}>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            <tr>
              <th className="px-4 py-3 text-left">Nombre</th>
              <th className="px-4 py-3 text-left">Clave interna</th>
              <th className="px-4 py-3 text-right">Precio</th>
              <th className="px-4 py-3 text-center">Socios</th>
              <th className="px-4 py-3 text-center">Origen</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {types.length === 0 ? (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-slate-400">Sin tipos registrados</td></tr>
            ) : types.map((t) => (
              <tr key={t.id} data-testid={`tipo-row-${t.key}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-bold">{t.label}</td>
                <td className="px-4 py-3 text-slate-500 font-mono text-xs">{t.key}</td>
                <td className="px-4 py-3 text-right font-display font-bold text-[#16a34a]">{fmtEUR(t.price)}</td>
                <td className="px-4 py-3 text-center">
                  <span className="inline-block px-2 py-0.5 bg-slate-100 text-slate-700 font-bold text-xs" style={{ borderRadius: 4 }}>
                    {t.usage_count}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  {t.is_builtin ? (
                    <span className="text-[10px] tracking-widest uppercase text-slate-500 font-bold">Predeterminado</span>
                  ) : (
                    <span className="text-[10px] tracking-widest uppercase text-[#007AFF] font-bold">Personalizado</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  <button
                    data-testid={`edit-tipo-${t.key}`}
                    onClick={() => openEdit(t)}
                    className="inline-flex items-center gap-1 text-slate-600 hover:text-[#007AFF] text-xs font-bold uppercase tracking-wide"
                  >
                    <PencilSimple size={14} /> Editar
                  </button>
                  <button
                    data-testid={`delete-tipo-${t.key}`}
                    onClick={() => remove(t)}
                    disabled={t.is_builtin || t.usage_count > 0}
                    className="inline-flex items-center gap-1 text-[#EF4444] hover:text-red-700 text-xs font-bold uppercase tracking-wide disabled:text-slate-300 disabled:cursor-not-allowed"
                    title={t.is_builtin ? "Los predeterminados no se eliminan" : t.usage_count > 0 ? `${t.usage_count} socio(s) lo usan` : ""}
                  >
                    <Trash size={14} /> Borrar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-500 space-y-1">
        <p>• Los tipos <strong>Predeterminados</strong> se pueden editar (etiqueta y precio) pero no eliminar, para no romper socios existentes.</p>
        <p>• Un tipo <strong>Personalizado</strong> solo se puede borrar si ningún socio lo tiene asignado actualmente.</p>
        <p>• La clave interna se genera automáticamente a partir del nombre y nunca cambia (así los socios ya registrados mantienen su tipo si lo renombras).</p>
      </div>

      {/* Modal form */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={close}>
          <div className="bg-white w-full max-w-md" style={{ borderRadius: 8 }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h2 className="font-display font-bold text-xl uppercase tracking-tighter">
                {editing ? "Editar tipo" : "Nuevo tipo"}
              </h2>
              <button onClick={close} className="text-slate-400 hover:text-slate-600" data-testid="close-tipo-form">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Nombre *</label>
                <input
                  data-testid="tipo-label-input"
                  type="text"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="Ej. Veterano"
                  className="w-full mt-1 bg-white border border-slate-200 px-3 h-11 focus:border-[#2563EB] outline-none"
                  style={{ borderRadius: 6 }}
                  required
                  autoFocus
                />
                {editing && editing.is_builtin && (
                  <p className="text-[11px] text-slate-500 mt-1">Clave interna: <strong>{editing.key}</strong> (no cambia al editar)</p>
                )}
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Precio (€) *</label>
                <input
                  data-testid="tipo-price-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="w-full mt-1 bg-white border border-slate-200 px-3 h-11 focus:border-[#2563EB] outline-none"
                  style={{ borderRadius: 6 }}
                  required
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={close} className="px-4 h-10 text-slate-600 font-bold text-sm uppercase tracking-wide">Cancelar</button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="submit-tipo-form"
                  className="bg-[#007AFF] hover:bg-[#005bb5] text-white px-5 h-10 flex items-center gap-2 font-bold text-sm uppercase tracking-wide disabled:opacity-50"
                  style={{ borderRadius: 6 }}
                >
                  <CheckCircle size={16} weight="bold" /> {editing ? "Guardar" : "Crear"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
